import { TextField, InputAdornment } from '@mui/material'
import { COLORS } from 'styles/colors'

type InputProps = {
	value: string
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
	isMultiline?: boolean
	label: string
	maxLength?: number
	rows?: number
	minRows?: number
	maxRows?: number
	width?: string
	height?: string
	isDisable?: boolean
	isError?: boolean
	helperText?: string
	endAdornment?: React.ReactNode
	startIcon?: React.ReactNode
	placeholder?: string
	size?: 'small' | 'medium'
	hideLabel?: boolean
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>
	sx?: object
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>

const Input = (props: InputProps) => {
	const {
		value,
		onChange,
		isMultiline = false,
		label,
		rows,
		minRows,
		maxRows,
		maxLength,
		width,
		height,
		isDisable,
		isError,
		helperText,
		endAdornment,
		startIcon,
		placeholder,
		inputProps,
		size,
		hideLabel,
		sx,
	} = props
	const isAutoHeightMultiline =
		isMultiline && (minRows !== undefined || maxRows !== undefined)
	const currentHeight = isAutoHeightMultiline
		? 'auto'
		: height
			? height
			: size === 'small'
				? '40px'
				: '54px'
	const hasValue = Boolean(value && value.length > 0)
	const inputPaddingLeft = hasValue || !startIcon ? '10px' : '32px'
	const labelPaddingLeft = hasValue || !startIcon ? '0px' : '25px'
	return (
		<TextField
			error={isError}
			multiline={isMultiline}
			variant='outlined'
			size={size}
			label={hideLabel ? undefined : label}
			helperText={helperText}
			disabled={isDisable}
			rows={isAutoHeightMultiline ? undefined : rows}
			minRows={isAutoHeightMultiline ? (minRows ?? 3) : undefined}
			maxRows={isAutoHeightMultiline ? (maxRows ?? 16) : undefined}
			value={value}
			placeholder={placeholder}
			sx={{
				opacity: !isDisable ? 1 : 0.5,
				width: width ? width : '100%',
				height: currentHeight,
				'.MuiFormHelperText-root': {
					color: isError ? COLORS.error : COLORS.icon,
					width: '300px',
					margin: '0px',
				},
				'& label': {
					fontSize: value ? '16px' : '14px',
					paddingLeft: labelPaddingLeft,
				},
				'& label.Mui-focused': {
					color: `${COLORS.main}`,
					fontSize: '16px',
				},
				'& .MuiInputBase-input': {
					fontSize: '14px',
					paddingLeft: hideLabel
						? '8px'
						: isMultiline
							? '0px'
							: inputPaddingLeft,
					...(hideLabel
						? { textAlign: 'center', height: '100%', boxSizing: 'border-box' }
						: {}),
					...(isMultiline && !hideLabel
						? { paddingTop: 0, paddingBottom: 0 }
						: {}),
				},
				'& .MuiInputBase-input::placeholder': hideLabel
					? {
							opacity: 1,
							color: COLORS.secondary,
						}
					: undefined,
				'& .MuiOutlinedInput-root': {
					borderRadius: '8px',
					height: currentHeight,
					...(isMultiline && !hideLabel
						? {
								paddingTop: '12px',
								paddingBottom: '12px',
								paddingLeft: inputPaddingLeft,
								paddingRight: '10px',
							}
						: {}),
					'& fieldset': {
						border: `1px solid ${COLORS.inputBorder}`,
						...(hideLabel ? { top: 0 } : {}),
					},
					'& legend': hideLabel
						? {
								display: 'none',
							}
						: undefined,
					'&:hover fieldset': {
						opacity: !isDisable ? 1 : 0.5,
						border: `1px solid ${COLORS.hoverInputBorder}`,
					},
					'&.Mui-focused fieldset': {
						border: `1px solid ${COLORS.main}`,
					},
				},
				...sx,
			}}
			onChange={onChange}
			InputProps={{
				startAdornment: startIcon ? (
					<InputAdornment position='start' sx={{ p: 0, m: 0 }}>
						{startIcon}
					</InputAdornment>
				) : undefined,
				endAdornment,
			}}
			inputProps={{
				...inputProps,
				maxLength: maxLength ?? inputProps?.maxLength,
				style: { fontSize: '14px', ...inputProps?.style },
			}}
		/>
	)
}

export default Input
